/* eslint-disable jsx-a11y/alt-text */
import classNames from 'classnames'
import { Icon } from 'components/common/Icon'
import useTranslation from 'next-translate/useTranslation'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import { useCallback, useEffect, useRef, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import { TImages, getImageUrl } from 'utils/image'
import { tw } from 'utils/strings'
import { useOverlay } from '../../contexts'
const ImageLightbox = dynamic(
  () => import('components/common/ImageLightbox').then(m => m.ImageLightbox),
  { ssr: false }
)

const MobileLoader = () => (
  <div className='relative h-full w-full'>
    <Skeleton width='100%' height='100%' className='absolute left-0 top-0' />
  </div>
)

const DesktopLoader = () => (
  <div className='h-full w-full lg:pt-[100%]'>
    <Skeleton width='100%' height='100%' className='absolute left-0 top-0' />
    <Image src='/files/invisible.png' alt='' fill sizes='1vw' />
  </div>
)

type Props = {
  images?: TImages
  className?: string
  alt?: string
  mobileOnly?: boolean
  imageContainerClassName?: string
}

type SlidesProps = {
  alt?: string
  hasThumbnails?: boolean
  slideIndex?: number
  setSlideIndex?: React.Dispatch<React.SetStateAction<number>>
  onSlideClick?: React.MouseEventHandler<HTMLDivElement>
} & Props

const Slides = ({
  className,
  alt,
  images,
  hasThumbnails = false,
  slideIndex = 0,
  setSlideIndex,
  onSlideClick,
  mobileOnly = false,
  imageContainerClassName,
}: SlidesProps) => {
  const { t } = useTranslation('common')
  const [currentSlideIndex, setCurrentSlideIndex] = useState(slideIndex)
  const [imgLoaded, setImgLoaded] = useState(false)
  const hasOnlyOneImage = images?.length === 1

  const touchStartX = useRef(0)
  const touchEndX = useRef(0)

  useEffect(() => {
    setCurrentSlideIndex(slideIndex)
  }, [slideIndex])

  const setSlideIndexHandler = useCallback(
    (value: number) => {
      setCurrentSlideIndex(value)
      setSlideIndex?.(value)
    },
    [setSlideIndex]
  )

  const nextSlide = () => {
    if (!images) return

    if (currentSlideIndex === images.length - 1) {
      setSlideIndexHandler(0)
    } else {
      setSlideIndexHandler(currentSlideIndex + 1)
    }
  }

  const previousSlide = () => {
    if (!images) return

    if (currentSlideIndex === 0) {
      setSlideIndexHandler(images.length - 1)
    } else {
      setSlideIndexHandler(currentSlideIndex - 1)
    }
  }

  const checkSwipe = () => {
    if (touchEndX.current < touchStartX.current) {
      nextSlide()
    }

    if (touchEndX.current > touchStartX.current) {
      previousSlide()
    }

    touchEndX.current = 0
    touchStartX.current = 0
  }

  return (
    <div
      className={classNames(
        className,
        'h-[260px] w-full sm:h-[300px]',
        !mobileOnly && 'lg:h-full'
      )}
    >
      <div
        className={classNames(
          imageContainerClassName,
          'relative -mx-6 flex h-full w-[calc(100%+3rem)] flex-col border-cool-300 bg-warm-50 px-6 py-4 sm:-mx-12 sm:w-[calc(100%+6rem)] sm:px-12',
          !mobileOnly && ' lg:mx-0 lg:w-full lg:border lg:bg-cool-50 lg:p-10'
        )}
        onTouchStart={e => {
          touchStartX.current = e.changedTouches[0].screenX
        }}
        onTouchEnd={e => {
          touchEndX.current = e.changedTouches[0].screenX
          checkSwipe()
        }}
      >
        {!images && (
          <>
            <div className={classNames(!mobileOnly && 'lg:hidden')}>
              <MobileLoader />
            </div>

            <div
              className={classNames(
                'hidden w-full',
                !mobileOnly && 'lg:block lg:p-10 xl:p-20'
              )}
            >
              <div className='relative flex w-full'>
                <DesktopLoader />
              </div>
            </div>
          </>
        )}

        {images && (
          <div className='flex h-full w-full items-center justify-between'>
            {images && !hasOnlyOneImage && (
              <button
                type='button'
                onClick={previousSlide}
                aria-label={t('previous_image')}
              >
                <Icon
                  name='arrow-chevron-back'
                  size={24}
                  className={classNames(
                    'transition-color text-cool-600 duration-200 ease-in-out',
                    !mobileOnly && 'lg:text-cool-300 lg:hover:text-cool-900'
                  )}
                />
              </button>
            )}
            <div
              className={classNames('w-full', !mobileOnly && 'lg:p-10 xl:p-20')}
            >
              {images?.map((image, index) => {
                const url = getImageUrl(image, 'large')

                const imgProps = {
                  src: url,
                  alt: alt || `Artwork - ${url}`,
                  priority: index === 0,
                  loading: index === 0 ? 'eager' : 'lazy',
                  onLoadingComplete: () => {
                    setImgLoaded(true)
                  },
                } as const

                return (
                  <div
                    className={
                      index === currentSlideIndex
                        ? 'active-pic opacity-100 duration-700 ease-in-out'
                        : 'opacity-0'
                    }
                    key={index}
                  >
                    {index === currentSlideIndex && (
                      <div
                        onClick={onSlideClick}
                        className={
                          index === 0
                            ? 'drop-shadow-[0_4px_5px_rgba(38,35,56,0.15)]'
                            : ''
                        }
                      >
                        <div className='relative flex h-full items-center justify-center'>
                          {!imgLoaded && (
                            <div
                              className={classNames(
                                !mobileOnly ? 'block' : 'jidden'
                              )}
                            >
                              <DesktopLoader />
                            </div>
                          )}

                          <Image
                            {...imgProps}
                            className={classNames(
                              'hidden aspect-1 cursor-zoom-in object-contain transition-all duration-500',
                              !mobileOnly && 'lg:block'
                            )}
                            width={500}
                            height={500}
                            sizes={getImageSizes('desktop', mobileOnly)}
                            quality={50}
                          />

                          <Image
                            {...imgProps}
                            width={215}
                            height={215}
                            className={classNames(
                              'aspect-1 object-contain',
                              !mobileOnly && 'lg:hidden'
                            )}
                            sizes={getImageSizes('mobile', mobileOnly)}
                            quality={50}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {images && !hasOnlyOneImage && (
              <button
                type='button'
                onClick={nextSlide}
                aria-label={t('next_image')}
              >
                <Icon
                  name='arrow-chevron-forward'
                  size={24}
                  className={classNames(
                    'transition-color text-cool-600 duration-200 ease-in-out',
                    !mobileOnly && 'lg:text-cool-300 lg:hover:text-cool-900'
                  )}
                  onClick={nextSlide}
                />
              </button>
            )}
          </div>
        )}

        {images && !hasOnlyOneImage && (
          <div
            className={classNames(
              'mt-2.5 flex w-full items-center justify-center space-x-3',
              !mobileOnly && 'lg:hidden'
            )}
          >
            {images.map((_, index) => (
              <button
                type='button'
                key={index}
                className={classNames(
                  'h-[3px] w-full max-w-[22px] rounded-full',
                  index === currentSlideIndex ? 'bg-cool-900' : 'bg-cool-400'
                )}
                onClick={() => setSlideIndexHandler(index)}
                aria-label={t('go_to_slide_x', { x: index })}
              ></button>
            ))}
          </div>
        )}
      </div>

      {images && hasThumbnails && !hasOnlyOneImage && (
        <>
          <div className='flex justify-center'>
            <div
              className={classNames(
                'relative mt-9 hidden flex-wrap items-center',
                !mobileOnly && 'lg:flex lg:justify-center'
              )}
            >
              {images.map((image, index) => {
                const url = getImageUrl(image)

                return (
                  <div
                    key={index}
                    className={`relative mx-4 my-4 flex h-28 w-28 cursor-pointer items-center justify-center transition-all duration-200 ease-in-out hover:scale-110 ${
                      currentSlideIndex === index
                        ? 'transition-all duration-200 ease-in-out'
                        : 'opacity-40 transition-all duration-200 ease-in-out hover:opacity-100'
                    }`}
                  >
                    <Image
                      src={url}
                      key={index}
                      alt={`${url} - ${index}`}
                      onClick={() => setSlideIndexHandler(index)}
                      className='aspect-1 object-contain object-center'
                      width={112}
                      height={112}
                      sizes='(max-width: 1023px) 0vw,
                              (min-width: 1024px) 10vw'
                    />
                  </div>
                )
              })}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export const SlideShow = ({ images, className, ...props }: Props) => {
  const [slideIndex, setSlideIndex] = useState(0)
  const { setIsOpen, setConfig } = useOverlay()

  useEffect(() => {
    setSlideIndex(0)
  }, [images])

  return (
    <div className={className}>
      <Slides
        {...props}
        images={images}
        hasThumbnails
        slideIndex={slideIndex}
        setSlideIndex={setSlideIndex}
        onSlideClick={() => {
          setConfig({
            className: tw`flex items-center justify-center !bg-opacity-80 [&>*]:!p-0 [&>*]:!h-full`,
            hasIcon: true,
            body: (
              <ImageLightbox
                slideIndex={slideIndex}
                setSlideIndex={setSlideIndex}
                images={images || []}
              />
            ),
          })
          setIsOpen(true)
        }}
      />
    </div>
  )
}

function getImageSizes(device: 'desktop' | 'mobile', mobileOnly?: boolean) {
  if (device === 'desktop') {
    if (mobileOnly) return '0vw'
    return '(min-width: 1023px) 30vw, 0vw'
  }

  if (device === 'mobile') {
    if (mobileOnly) return '50vw'
    return '(max-width: 1024px) 50vw, 0vw'
  }

  return ''
}
